import { call } from '@advanza/api'
import { Icon } from '@advanza/ui'
import BigPicker from '@trustoo/components/searchPicker/BigPicker'
import { mapLocationDataToOption } from '@trustoo/components/searchPicker/funcs/mapLocationDataToOption'
import {
    SearchPickerOption,
    SearchPickerSettings,
} from '@trustoo/components/searchPicker/SearchPickerContext'
import { GeoIdLocation } from '@trustoo/types/api/controllers/geo'
import clsx from 'clsx'
import { useMemo } from 'react'

interface SearchLocationInputProps {
    value?: string
    limit?: number
    countryCode?: string | false
    withIcon?: boolean
    onChangeLocation?: (location: GeoIdLocation) => void
    name?: string
    placeholder?: string
    disabled?: boolean
    error?: string | boolean
    className?: string
    /**
     * Border classes for the field, replacing the default. Pass this where the input sits next
     * to components from another design system, such as a shadcn select.
     */
    labelClassName?: string
    /**
     * The places listed while nothing is typed. Read once, when the input mounts.
     */
    initialOptions?: SearchPickerOption[]
    /**
     * Picker settings, replacing the shared defaults per key. `inputFieldHeight` is what a call
     * site reaches for: the picker's 46px field is taller than office's compact inputs.
     */
    settings?: Partial<SearchPickerSettings>
}

export function findLocation(
    query = '',
    countryCode: string | false = false,
    limit = 10,
): Promise<{ results: GeoIdLocation[] }> {
    return call('geo/geo-id-results/', {
        query: {
            query,
            countryCode,
            limit,
        },
    })
}

const SearchLocationInput = ({
    value,
    limit = 10,
    countryCode = false,
    withIcon,
    onChangeLocation,
    name,
    placeholder = 'Place',
    disabled,
    error,
    className,
    labelClassName,
    initialOptions,
    settings,
}: SearchLocationInputProps) => {
    const searchForLocation = async (query: string) => {
        const { results } = await findLocation(query, countryCode, limit)
        return results.map(mapLocationDataToOption)
    }

    // Memoized so the picker's selectedOption-sync effect only fires when `value` actually
    // changes, instead of resetting the typed query on every render.
    const selectedOption = useMemo<SearchPickerOption | undefined>(
        () => (value ? { id: value, content: value, query: value } : undefined),
        [value],
    )

    // Mirrors the border of the office inputs around this one: advanza's grey while empty,
    // darkening once the field holds a value or takes focus. The `!` is what makes it stick —
    // the shared InputField sets a border colour of its own and neither rule wins on specificity.
    const defaultLabelClassName = clsx(
        !error && (value ? '!border-[#10185a]' : '!border-[#dbdce0]'),
        !error && 'focus-within:!border-[#10185a]',
    )

    const handleSelectOption = (option: SearchPickerOption) => {
        const location = option.data as GeoIdLocation | undefined
        if (location) {
            onChangeLocation?.(location)
        }
    }

    return (
        <BigPicker
            fetchFunction={searchForLocation}
            selectedOption={selectedOption}
            onSelectOption={handleSelectOption}
            initialQuery={value ?? ''}
            initialOptions={initialOptions}
            // The field holds the saved place, so text that did not become one must not linger.
            settings={{ clearUnselectedQuery: true, ...settings }}
            inputFieldProps={{
                before: withIcon ? <Icon name="place" fontSize={21} /> : undefined,
                placeholder,
                disabled,
                name,
                error,
                // The picker opens on the place it already holds, so a click meaning "edit this"
                // would drop a caret mid-word. Selecting lets the next keystroke replace it.
                onClick: (e) => e.currentTarget.select(),
                labelClassName: labelClassName ?? defaultLabelClassName,
            }}
            className={className}
        />
    )
}

export default SearchLocationInput
